/**
 * Consent-aware Google Ads conversion tracking (direct gtag, ADVANCED consent).
 *
 * ADVANCED Consent Mode behaviour (Google's standard setup):
 *   - the Google Ads tag LOADS AT STARTUP, before any consent choice, while
 *     the denied Consent Mode v2 defaults (index.html) are in effect; the
 *     visitor's choice upgrades/downgrades storage via gtag consent update
 *   - conversion events are ALWAYS sent; when consent is denied, Consent Mode
 *     anonymizes the ping (no cookies, ads_data_redaction) so Google still
 *     receives a cookie-less measurement signal
 *   - GPC still forces advertising consent denied upstream
 *     (`src/lib/consent.ts`) — denied means anonymized, not unsent
 *   - the tag still never initializes on private/unknown routes
 *     (`vendorsAllowedHere`), whatever the consent state
 *
 * This module owns ONLY the Google Ads conversion tag. GA4 and GTM ownership
 * is unchanged and still lives in `src/lib/consent.ts`.
 *
 * Payload contract: our conversion event sends `send_to` and a random,
 * non-PII `transaction_id` and nothing else. Enhanced conversions are
 * disabled in the Ads UI and in our tag config, so we never attach user data.
 * Note: the Google tag still performs its own standard metadata collection
 * (cookies / first-party identifiers, page URL, referrer, user agent,
 * IP-derived coarse location). `send_page_view: false` only stops OUR
 * page_view command; it is not a claim that the tag makes no network calls or
 * no remarketing-capable collection. Remarketing behaviour is governed by the
 * Google Ads account settings and Consent Mode signals, not by this flag.
 */

import { getConsent, subscribeConsent, advertisingSuppressed, vendorsAllowedHere } from "./consent";
import { configureGoogleTagDestination } from "./google-tag";
import { applySharedPageContext, buildPageContext, safeCampaignFields } from "./page-context";
import { markVendorLoaded } from "./vendor-route-boundary";
import {
  GOOGLE_ADS_CONVERSION_ID,
  getConversionSendTo,
  isAdsAccountConfigured,
  type AdsConversionKey,
} from "./ads-config";

let adsTagLoaded = false;
let initialized = false;
let unsubscribe: (() => void) | null = null;

/** Reset internal state. Test-only. */
export function __resetAdsTrackingForTests(): void {
  adsTagLoaded = false;
  initialized = false;
  callConversionConfigured = false;
  unsubscribe?.();
  unsubscribe = null;
}

/**
 * True only when advertising may run right now: consent granted, no GPC, no
 * stored "Do Not Sell or Share" device opt-out, and the visitor is on a public
 * trackable route (a vendor must never observe a private/token route).
 */
function advertisingAllowed(): boolean {
  const c = getConsent();
  if (!c?.advertising || c?.gpc === true) return false;
  if (advertisingSuppressed()) return false;
  return vendorsAllowedHere();
}

/**
 * Load + config the Ads tag exactly once. Advanced Consent Mode: no consent
 * precondition — the tag loads under the denied defaults and consent updates
 * refine it afterwards. Still gated to public trackable routes.
 */
function ensureAdsTag(): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") return false;
  if (!isAdsAccountConfigured()) return false;
  if (!vendorsAllowedHere()) return false;
  if (adsTagLoaded) return true;

  const id = GOOGLE_ADS_CONVERSION_ID as string;
  // GA4 and Ads are SEPARATE Google tags now: the shared bootstrap loads this
  // destination's own loader and configures it explicitly, so analytics-first
  // and ads-first grant orders converge and a denied destination is never
  // initialized. The shared SAFE page context is set BEFORE the config so the
  // tag's automatic collection inherits sanitized page fields. We send no
  // page_view of our own and attach no user data; enhanced conversions stay
  // off here and in the Ads UI.
  const ctx = applySharedPageContext();
  if (!configureGoogleTagDestination(id, {
    send_page_view: false,
    allow_enhanced_conversions: false,
    ...(ctx ?? {}),
    ...safeCampaignFields(),
  })) return false;
  adsTagLoaded = true;
  markVendorLoaded();
  return true;
}

/**
 * Initialize Ads tracking for the session. Call once on app start.
 *
 * Advanced Consent Mode: loads the tag immediately at startup under the
 * denied defaults (so the ad click is attributed on the landing page), and
 * subscribes so any later route/consent change can still initialize it.
 */
export function initAdsTracking(): void {
  if (typeof window === "undefined") return;
  if (initialized) return;
  initialized = true;
  if (!isAdsAccountConfigured()) return;

  ensureAdsTag();
  if (adsTagLoaded) return;

  unsubscribe = subscribeConsent(() => {
    if (adsTagLoaded) return;
    if (ensureAdsTag()) {
      unsubscribe?.();
      unsubscribe = null;
    }
  });
}

const TRANSACTION_ID_RE = /^[A-Za-z0-9-]{8,64}$/;

export interface AdsConversionOptions {
  /** Random, non-PII de-duplication id. Rejected if it looks like anything else. */
  transactionId: string;
  /**
   * Called once the tag reports the hit was handed off (Google's documented
   * `event_callback`), or when `event_timeout` elapses. Never called more than
   * once. Callers that navigate away immediately use this to give the beacon
   * a bounded chance to leave.
   */
  onDispatched?: () => void;
}

/**
 * Send one Google Ads conversion. Returns true only if a send actually
 * happened. The event is ALWAYS sent (advanced Consent Mode): when consent
 * is denied, Consent Mode anonymizes the ping. Returns false (silently, by
 * design) only when the account/label is not configured, the route is not a
 * public trackable one, or the transaction id is not a safe random token.
 */
export function sendAdsConversion(key: AdsConversionKey, opts: AdsConversionOptions): boolean {
  if (typeof window === "undefined") return false;
  if (!vendorsAllowedHere()) return false;

  const sendTo = getConversionSendTo(key);
  if (!sendTo) return false; // unknown ID or label — never guess.

  const txn = opts.transactionId;
  if (!txn || !TRANSACTION_ID_RE.test(txn)) return false;

  if (!ensureAdsTag()) return false;
  const gtag = typeof window.gtag === "function" ? window.gtag : null;
  if (!gtag) return false;

  try {
    let called = false;
    const once = opts.onDispatched
      ? () => {
          if (called) return;
          called = true;
          opts.onDispatched?.();
        }
      : undefined;
    // Safe page context on the direct conversion too — same sanitized fields
    // every other Google call carries, never the raw URL.
    gtag("event", "conversion", {
      ...buildPageContext(),
      send_to: sendTo,
      transaction_id: txn,
      ...(once ? { event_callback: once, event_timeout: 700 } : {}),
    });
    return true;
  } catch {
    return false;
  }
}

/** The call destination (`AW-…/LABEL`) is a config target, never a loader id. */
let callConversionConfigured = false;

/**
 * Configure the Google Ads CALL conversion destination (call-from-website).
 *
 * Exposed for `src/lib/call-tracking.ts` only. Runs under exactly the same
 * advertising-consent / GPC / public-route boundary as every other Ads call.
 *
 * IMPORTANT: `AW-18431332404/FUQbCOD_6PIcELSg39RE` is a conversion-action
 * destination, NOT a tag id. It must never be handed to the loader — doing so
 * inserts a second `gtag/js` script for a nonexistent tag id. We ensure the
 * already-consented BASE `AW-18431332404` tag (one loader, one `js` command)
 * and then issue a single `gtag('config', <AW-id/label>, …)`.
 *
 * We never send a conversion event for a phone click: Google measures the
 * forwarded call itself, including the 60-second minimum.
 */
export function configureCallConversion(
  sendTo: string,
  params: Record<string, unknown>,
): boolean {
  if (typeof window === "undefined") return false;
  if (!advertisingAllowed()) return false;
  if (!sendTo) return false;
  // Reuse (or, on first use, load) the BASE Ads tag only.
  if (!ensureAdsTag()) return false;
  if (callConversionConfigured) return true;

  const gtag = typeof window.gtag === "function" ? window.gtag : null;
  if (!gtag) return false;

  // Only the two documented call-tracking params are forwarded, type-checked.
  const number = params.phone_conversion_number;
  const callback = params.phone_conversion_callback;
  const safe: Record<string, unknown> = {};
  if (typeof number === "string" && number.trim()) safe.phone_conversion_number = number;
  if (typeof callback === "function") safe.phone_conversion_callback = callback;
  if (!safe.phone_conversion_number) return false;

  try {
    gtag("config", sendTo, {
      ...buildPageContext(),
      ...safe,
      allow_enhanced_conversions: false,
    });
  } catch {
    return false;
  }
  callConversionConfigured = true;
  markVendorLoaded();
  return true;
}

/** True when advertising may run right now. Exposed for the call module. */
export function isAdvertisingAllowedNow(): boolean {
  return advertisingAllowed();
}

